import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import {
    DEFAULT_PRODUCT_SORT,
    PRODUCT_SORT_OPTIONS,
    ProductSortKey,
    ProductSortOption,
} from '../utils/productSort';

type Props = {
    value?: ProductSortKey;
    onChange: (key: ProductSortKey) => void;
    className?: string;
    /** Optional id for aria labelling */
    id?: string;
    /** Override options (e.g. order lists). Default: catalog PRODUCT_SORT_OPTIONS. */
    options?: readonly ProductSortOption[];
    ariaLabelKey?: string;
    ariaLabelFallback?: string;
};

/**
 * LanguageSwitcher-style pill dropdown for product sort modes.
 * Colors from SiteSettings CSS vars only.
 */
const ProductSortDropdown: React.FC<Props> = ({
    value = DEFAULT_PRODUCT_SORT,
    onChange,
    className = '',
    id = 'product-sort',
    options = PRODUCT_SORT_OPTIONS,
    ariaLabelKey = 'catalog.sort.aria',
    ariaLabelFallback = 'Sort products',
}) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const current =
        options.find((o) => o.key === value) ?? options[0] ?? PRODUCT_SORT_OPTIONS[0];
    const triggerLabel = tr(current.labelKey, current.labelFallback);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className={`relative shrink-0 ${className}`}>
            <button
                type="button"
                id={id}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={tr(ariaLabelKey, ariaLabelFallback)}
                className="inline-flex min-h-11 max-w-[min(100%,16rem)] items-center gap-1 truncate rounded-full bg-[var(--accent-color)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[color-mix(in_srgb,var(--accent-color),black_20%)] sm:max-w-none sm:px-5 sm:py-2 sm:text-sm"
            >
                <span className="truncate">{triggerLabel}</span>
                <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 opacity-70 transition ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>

            {open && (
                <ul
                    role="listbox"
                    aria-labelledby={id}
                    // Mobile: open under/left-aligned to trigger (sales/purchases sort is left-side).
                    // Desktop: keep right-align so toolbar menus stay flush with the pill.
                    className="absolute left-0 z-50 mt-2 min-w-[12rem] overflow-hidden rounded-2xl border border-[var(--accent-color)]/40 bg-[var(--bg-color)] py-1 shadow-xl lg:left-auto lg:right-0"
                >
                    {options.map((opt) => {
                        const selected = opt.key === value;
                        const label = tr(opt.labelKey, opt.labelFallback);
                        return (
                            <li key={opt.key} role="option" aria-selected={selected}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(opt.key);
                                        setOpen(false);
                                    }}
                                    className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_55%)] ${
                                        selected
                                            ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_40%)] font-semibold text-[var(--text-color)]'
                                            : 'text-[var(--text-color)]'
                                    }`}
                                >
                                    <span className="truncate">{label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default ProductSortDropdown;
